/**
 * The touch-move rule's legality gate — the old screen's
 * `canMoveRestrictedPiece` (PlayChess/index.tsx L5995-6011, index.js
 * L4159-4176): a piece may only become the `restrictedPiece` when it has at
 * least one legal move in the LIVE position, because a piece that cannot move
 * would lock the player out of the whole board.
 *
 * The colour check is here too, and it is NOT redundant: the board reports the
 * piece it drew, which during a turn-race can be the opponent's (old L6015-6020
 * says why the guard exists).
 *
 * chess.js is only ever handed a FEN (never a mutable shared board — same
 * contract as `chess/san.ts`) and THROWS on an invalid FEN or square, so the
 * call is wrapped.
 */
import {Chess} from 'chess.js';

/**
 * @param fen the LIVE position (`game.fen`)
 * @param square the square the board reported
 * @param myColor my side, as chess.js spells it
 * @returns true when the piece on `square` is mine AND has a legal move.
 *   chess.js generates moves for the side to move only, so this is false on the
 *   opponent's turn — which is the old behaviour (`game.moves({square})`).
 */
export function canRestrict(
  fen: string,
  square: string,
  myColor: 'w' | 'b' | null,
): boolean {
  if (!fen || !square || !myColor) {
    return false;
  }
  try {
    const game = new Chess(fen);
    const piece = game.get(square as any);
    if (!piece || piece.color !== myColor) {
      return false;
    }
    return game.moves({square} as any).length > 0;
  } catch (e) {
    return false;
  }
}
